import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import i18n, { setLanguage } from './i18n';
import AlertNotification from './components/ui/AlertNotification.vue';

// Import both styles (Bulma with old import, our styles with new @use)
import './styles/main-bulma.scss';
import './styles/main.scss';

// Create app
const app = createApp(App);

// Add global properties
app.provide('APP_NAME', 'Indiefy');
app.provide('APP_VERSION', '1.0.0');

// Use plugins
const pinia = createPinia();
app.use(pinia);
app.use(router);
app.use(i18n);

// Register global components
app.component('AlertNotification', AlertNotification);

// Check for stored language, query parameter, or use browser language
const supportedLanguages = ['en', 'es', 'pt', 'tr', 'hi', 'fr'];
const storedLanguage = localStorage.getItem('indiefy-language');
const urlParams = new URLSearchParams(window.location.search);
const langParam = urlParams.get('lang');

if (storedLanguage) {
  setLanguage(storedLanguage);
} else if (langParam) {
  setLanguage(langParam);
} else {
  // Get browser language and clean it up
  let browserLang = window.navigator.userLanguage || window.navigator.language;
  browserLang = browserLang.split('-')[0].split('_')[0].toLowerCase();

  // Use browser language if supported, otherwise fallback to English
  const lang = supportedLanguages.includes(browserLang) ? browserLang : 'en';
  setLanguage(lang);
}

// Initialize theme from localStorage
const savedTheme = localStorage.getItem('indiefy-theme');
if (savedTheme === 'dark') {
  document.documentElement.classList.add('dark-theme');
  document.body.classList.add('body--dark');
}

// Initialize authentication state
const initializeAuth = async () => {
  try {
    // Import auth store after pinia is set up
    const { useAuthStore } = await import('./store/auth');
    const authStore = useAuthStore();

    // Check if there's a stored user session
    const storedUser = localStorage.getItem('indiefy-user');
    // console.log('🚀 Auth initialization:', { hasStoredUser: !!storedUser });

    if (storedUser) {
      try {
        // Parse stored user data to set initial state
        const userData = JSON.parse(storedUser);

        authStore.user = userData;
        authStore.isAuthenticated = true;

        // Check if user should be blocked immediately
        if (authStore.isUserBlocked(userData)) {
          console.log('🚫 User is blocked from stored data, should redirect');
        }

        // Verify session with server in the background
        // console.log('🔄 Verifying session with server...');
        await authStore.verifySession();
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        // Clear invalid stored data
        localStorage.removeItem('indiefy-user');
        authStore.clearSession();
        authStore.initializing = false;
      }
    } else {
      // No stored user, finish initialization
      authStore.initializing = false;
    }
  } catch (error) {
    console.error('Error initializing authentication:', error);
    // Make sure to finish initialization even on error
    try {
      const { useAuthStore } = await import('./store/auth');
      const authStore = useAuthStore();
      authStore.initializing = false;
    } catch (storeError) {
      console.error('Error accessing auth store:', storeError);
    }
  }
};

// Mount app and initialize auth
app.mount('#app');

// Initialize auth after app is mounted
initializeAuth();